// Eskom PARCC — dashboard drill-through model.
//
// Every headline figure on the cockpit is now a question you can open: click a
// KPI tile, a donut segment, a breach bar, a division bar or a recurring theme,
// and this builds the panel behind it — a headline stat, a breakdown, the
// underlying transactions, and a link into the register pre-filtered to exactly
// that slice.
//
// PURE. No React, no fetching: it takes the rows and findings the page has
// already computed and returns a plain model, so the maths is unit-testable and
// the two themes render the same numbers from the same function.
import type { Transaction } from '../../data/types';
import type { FacetRow, FacetState } from '../../domain/filters';
import { EMPTY_FACETS } from '../../domain/filters';
import { canonicalDivision } from '../../domain/divisions';
import { themeFor } from '../../domain/themes/themeRules';
import { formatRandCompact } from '../../lib/money';
import { BREACH_BUCKETS, type BreachBucketKey, type FindingLike } from './derive';
import { isPossibleIeLevel, isFindingLevel } from '../../domain/dashboard';
import { isSelfDeclaredLevel } from '../../domain/gaps';

/** How many underlying rows the panel lists before deferring to the register. */
export const DRILL_ROW_LIMIT = 7;

/**
 * What was clicked. Encoded as a string so a chart segment can carry it around
 * without a bespoke type per widget.
 *   kpi:<name>        a headline tile
 *   level:<level>     a compliance-distribution segment
 *   breach:<bucket>   a regulatory-breach bar
 *   division:<name>   a division-risk bar
 *   theme:<theme>     a recurring-findings row
 */
export type DrillKey = string;

export type DrillTone = 'ok' | 'warn' | 'bad' | 'neutral';

export interface DrillBar {
  label: string;
  value: number;
  /** 0–100, relative to the largest bar in the set. */
  pct: number;
  /** Pre-formatted value for the right-hand column. */
  display: string;
}

export interface DrillModel {
  key: DrillKey;
  title: string;
  subtitle: string;
  /** The headline number, already formatted. */
  stat: string;
  tone: DrillTone;
  breakdownTitle: string;
  breakdown: DrillBar[];
  /** Up to {@link DRILL_ROW_LIMIT} underlying transactions. */
  rows: Transaction[];
  /** How many transactions the slice actually contains. */
  totalRows: number;
  ctaLabel: string;
  /** Facets to pre-apply on the register (serialised into the CTA link). */
  ctaFacets: Partial<FacetState>;
}

export interface DrillContext {
  /** The rows already in view (facet-filtered), each with its effective level. */
  rows: readonly FacetRow[];
  /** The genuine findings already in view. */
  findings: readonly FindingLike[];
  /** The facets currently applied — carried through to the CTA link. */
  facets: FacetState;
}

// The IE tier list now comes from domain/dashboard (isPossibleIeLevel). The local
// copy that used to live here held only the two legacy levels, so any transaction on
// the newer "Possible Non-Compliance" taxonomy was missing from this panel while
// still counted in the tile it drilled into.

function levelOf(row: FacetRow): string {
  return row.compliance || row.tx.complianceLevel || 'Pending Review';
}

function isReviewed(tx: Transaction): boolean {
  if (tx.reviewed === true) return true;
  return (
    String(tx.reviewStatus ?? '')
      .trim()
      .toLowerCase() === 'reviewed'
  );
}

/** Build proportional bars from a label→count map, largest first. */
function bars(
  counts: ReadonlyMap<string, number>,
  format: (n: number) => string = (n) => n.toLocaleString('en-ZA'),
  limit = 8,
): DrillBar[] {
  const entries = [...counts.entries()].filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  const max = entries[0]?.[1] ?? 0;
  return entries.slice(0, limit).map(([label, value]) => ({
    label,
    value,
    pct: max > 0 ? Math.round((value / max) * 100) : 0,
    display: format(value),
  }));
}

/** Count rows per canonical division. */
function byDivision(rows: readonly FacetRow[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    const d = canonicalDivision(r.tx.division) || 'Unspecified';
    m.set(d, (m.get(d) ?? 0) + 1);
  }
  return m;
}

/** Count rows per effective compliance level. */
function byLevel(rows: readonly FacetRow[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    const l = levelOf(r);
    m.set(l, (m.get(l) ?? 0) + 1);
  }
  return m;
}

/** The transaction references a set of findings touches. */
function refsOf(findings: readonly FindingLike[]): Set<string> {
  const s = new Set<string>();
  for (const f of findings) if (f.referenceNo) s.add(f.referenceNo);
  return s;
}

/** Tone for a compliance level, matching the status vocabulary. */
function toneForLevel(level: string): DrillTone {
  const l = level.toLowerCase();
  if (l.includes('possible ie')) return 'bad';
  if (l.includes('policy') || l.includes('procedure')) return 'warn';
  if (l.includes('housekeeping')) return 'neutral';
  if (l.includes('not applicable') || l.includes('pending') || l.includes('not reviewed'))
    return 'neutral';
  if (l.includes('compliant') && !l.includes('non')) return 'ok';
  return 'neutral';
}

/**
 * Build the drill panel for a click. Returns null for an unrecognised key (a
 * chart handing back a segment we do not model yet), so the caller can simply
 * not open a panel rather than render an empty one.
 */
export function buildDrill(key: DrillKey, ctx: DrillContext): DrillModel | null {
  const [kind, ...rest] = key.split(':');
  const arg = rest.join(':');
  const all = ctx.rows;

  /** Assemble a model from a selected subset. */
  const model = (
    p: {
      title: string;
      subtitle: string;
      stat: string;
      tone: DrillTone;
      breakdownTitle?: string;
      breakdown?: DrillBar[];
      ctaLabel?: string;
      ctaFacets?: Partial<FacetState>;
    },
    subset: readonly FacetRow[],
  ): DrillModel => ({
    key,
    title: p.title,
    subtitle: p.subtitle,
    stat: p.stat,
    tone: p.tone,
    breakdownTitle: p.breakdownTitle ?? 'By division',
    breakdown: p.breakdown ?? bars(byDivision(subset)),
    rows: subset.slice(0, DRILL_ROW_LIMIT).map((r) => r.tx),
    totalRows: subset.length,
    ctaLabel: p.ctaLabel ?? `Open all ${subset.length.toLocaleString('en-ZA')} in the register`,
    ctaFacets: p.ctaFacets ?? {},
  });

  if (kind === 'kpi') {
    switch (arg) {
      case 'population':
        return model(
          {
            title: 'Population in view',
            subtitle: 'Every transaction matching the current filters.',
            stat: all.length.toLocaleString('en-ZA'),
            tone: 'neutral',
          },
          all,
        );
      case 'reviewed': {
        const subset = all.filter((r) => isReviewed(r.tx));
        return model(
          {
            title: 'Reviewed',
            subtitle: 'Transactions a reviewer has signed off.',
            stat: subset.length.toLocaleString('en-ZA'),
            tone: 'ok',
            breakdownTitle: 'By compliance outcome',
            breakdown: bars(byLevel(subset)),
            ctaFacets: { reviewed: 'Yes' },
          },
          subset,
        );
      }
      case 'pending': {
        const subset = all.filter((r) => !isReviewed(r.tx));
        return model(
          {
            title: 'Pending review',
            subtitle: 'Transactions still awaiting a reviewer.',
            stat: subset.length.toLocaleString('en-ZA'),
            tone: 'warn',
            ctaFacets: { reviewed: 'No' },
          },
          subset,
        );
      }
      case 'findings': {
        // ctx.findings is the OUTSTANDING set (self-declared / resolved excluded),
        // so this drill matches the "Findings" scorecard number it opens from.
        const refs = refsOf(ctx.findings);
        const subset = all.filter((r) => refs.has(r.tx.referenceNo));
        return model(
          {
            title: 'Findings',
            subtitle: `${ctx.findings.length.toLocaleString('en-ZA')} distinct findings still outstanding, across ${subset.length.toLocaleString('en-ZA')} transactions.`,
            stat: ctx.findings.length.toLocaleString('en-ZA'),
            tone: 'warn',
          },
          subset,
        );
      }
      // Possible-IE exposure, split by whether the gap is still unresolved or was
      // resolved by self-declaration (Lindiwe, 27 Jul 2026) — one panel per tile.
      case 'ie':
      case 'ie-self-declared': {
        const wantSelfDeclared = arg === 'ie-self-declared';
        const subset = all.filter((r) => {
          const l = levelOf(r);
          return isPossibleIeLevel(l) && isSelfDeclaredLevel(l) === wantSelfDeclared;
        });
        const value = subset.reduce((n, r) => n + Number(r.tx.value || 0), 0);
        return model(
          {
            title: wantSelfDeclared ? 'Possible IE · self-declared' : 'Possible IE · unresolved',
            subtitle: wantSelfDeclared
              ? `${subset.length.toLocaleString('en-ZA')} transactions whose possible irregular expenditure was resolved by self-declaration — declared, not recovered.`
              : `${subset.length.toLocaleString('en-ZA')} transactions carrying possible irregular expenditure with the gap still open.`,
            stat: formatRandCompact(value) || 'R 0',
            tone: wantSelfDeclared ? 'warn' : 'bad',
            breakdownTitle: 'Exposure by division',
            breakdown: bars(
              new Map(
                [...byDivisionValue(subset).entries()].map(([d, v]) => [d, Math.round(v)] as const),
              ),
              (n) => formatRandCompact(n) || 'R 0',
            ),
            ctaFacets: {
              compliance: wantSelfDeclared
                ? 'Self-Declared: Non-Compliance: Possible IE'
                : 'Non-Compliance: Possible IE',
            },
          },
          subset,
        );
      }
      case 'compliant':
      case 'non-compliant':
      case 'self-declared':
      case 'anomalies': {
        // These mirror computeDashboard's reviewed-outcome split exactly, so a tile and
        // its drill panel cannot disagree. Non-Compliant is now UNRESOLVED only, with
        // self-declarations in their own bucket; both predicates come from the domain
        // rather than substring tests — the old `includes('non-complian')` swept in
        // self-declarations AND missed the newer bare "Non-Conformance" level. Anomalies
        // is the residual reviewed outcome — Not Applicable plus the 'To delete'/'Deleted'
        // lifecycle sentinels — everything reviewed that is neither a finding nor strictly
        // Compliant (matches computeDashboard's anomalies = notApplicable + toBeDeleted).
        const want =
          arg === 'compliant'
            ? (l: string) => l === 'Compliant'
            : arg === 'anomalies'
              ? (l: string) => !isFindingLevel(l) && l !== 'Compliant'
              : arg === 'self-declared'
                ? (l: string) => isFindingLevel(l) && isSelfDeclaredLevel(l)
                : (l: string) => isFindingLevel(l) && !isSelfDeclaredLevel(l);
        const subset = all.filter((r) => isReviewed(r.tx) && want(levelOf(r)));
        const label =
          arg === 'compliant'
            ? 'Compliant'
            : arg === 'anomalies'
              ? 'Anomalies'
              : arg === 'self-declared'
                ? 'Self-Declared'
                : 'Non-Compliant';
        const subtitle =
          arg === 'self-declared'
            ? 'Reviewed transactions whose gap was resolved by self-declaration — they keep their severity tier.'
            : arg === 'non-compliant'
              ? 'Reviewed transactions carrying a gap that has NOT yet been resolved.'
              : arg === 'anomalies'
                ? 'Reviewed transactions that are Not Applicable or flagged to be deleted — set apart from Compliant.'
                : `Reviewed transactions assessed ${label}.`;
        return model(
          {
            title: label,
            subtitle,
            stat: subset.length.toLocaleString('en-ZA'),
            tone:
              arg === 'compliant'
                ? 'ok'
                : arg === 'anomalies'
                  ? 'neutral'
                  : arg === 'self-declared'
                    ? 'warn'
                    : 'bad',
            ctaFacets:
              arg === 'compliant'
                ? { compliance: 'Compliant', reviewed: 'Yes' }
                : { reviewed: 'Yes' },
          },
          subset,
        );
      }
      default:
        return null;
    }
  }

  if (kind === 'level') {
    const subset = all.filter((r) => levelOf(r) === arg);
    return model(
      {
        title: arg,
        subtitle: `${subset.length.toLocaleString('en-ZA')} of ${all.length.toLocaleString('en-ZA')} transactions in view.`,
        stat: subset.length.toLocaleString('en-ZA'),
        tone: toneForLevel(arg),
        ctaFacets: { compliance: arg },
      },
      subset,
    );
  }

  if (kind === 'breach') {
    const bucket = arg as BreachBucketKey;
    const anchors = BREACH_BUCKETS[bucket];
    if (!anchors) return null;
    const anchorSet = new Set<string>(anchors);
    // ctx.findings is the OUTSTANDING set, so this counts unresolved breach lines —
    // the same basis as the breach card's headline (Lindiwe R9).
    const hit = ctx.findings.filter((f) => (f.anchors ?? []).some((a) => anchorSet.has(a)));
    const refs = refsOf(hit);
    const subset = all.filter((r) => refs.has(r.tx.referenceNo));
    return model(
      {
        title:
          bucket === 'pfma'
            ? 'PFMA contraventions'
            : bucket === 'policy'
              ? 'Policy breaches'
              : 'Legislative / regulatory breaches',
        subtitle: `${hit.length.toLocaleString('en-ZA')} outstanding breach lines anchored to this framework, across ${subset.length.toLocaleString('en-ZA')} transactions.`,
        stat: hit.length.toLocaleString('en-ZA'),
        tone: bucket === 'pfma' ? 'bad' : bucket === 'policy' ? 'warn' : 'neutral',
      },
      subset,
    );
  }

  if (kind === 'division') {
    const subset = all.filter((r) => (canonicalDivision(r.tx.division) || 'Unspecified') === arg);
    return model(
      {
        title: arg,
        subtitle: `${subset.length.toLocaleString('en-ZA')} transactions in this operating unit.`,
        stat: subset.length.toLocaleString('en-ZA'),
        tone: 'neutral',
        // Drilling INTO a division, the useful cut is by compliance, not by division.
        breakdownTitle: 'By compliance level',
        breakdown: bars(byLevel(subset)),
        ctaFacets: { division: arg },
      },
      subset,
    );
  }

  if (kind === 'theme') {
    // `themeFor` returns a narrow union; the drill key arrives as a plain string
    // (it came from a chart label), so compare as strings.
    const hit = ctx.findings.filter((f) => (themeFor(f) as readonly string[]).includes(arg));
    const refs = refsOf(hit);
    const subset = all.filter((r) => refs.has(r.tx.referenceNo));
    return model(
      {
        title: arg,
        subtitle: `${subset.length.toLocaleString('en-ZA')} transactions exhibit this theme (${hit.length.toLocaleString('en-ZA')} finding lines).`,
        stat: subset.length.toLocaleString('en-ZA'),
        tone: 'warn',
      },
      subset,
    );
  }

  return null;
}

/** Total Rand value per canonical division. */
function byDivisionValue(rows: readonly FacetRow[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    const d = canonicalDivision(r.tx.division) || 'Unspecified';
    m.set(d, (m.get(d) ?? 0) + Number(r.tx.value || 0));
  }
  return m;
}

/**
 * Serialise the drill's CTA into a Transactions-register link. The register reads
 * these back into its facet state, so "open all 96 in the register" lands on
 * exactly the slice the panel described.
 */
export function drillLink(model: DrillModel, current: FacetState): string {
  const merged: FacetState = { ...EMPTY_FACETS, ...current, ...model.ctaFacets };
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(merged)) {
    if (value) params.set(key, value);
  }
  const qs = params.toString();
  return qs ? `/transactions?${qs}` : '/transactions';
}
